/**
 * Congrs brand color used for buttons when an event has no accent color set.
 */
export const DEFAULT_BUTTON_COLOR = "#E63E52";

/**
 * Parse a hex color (#RGB or #RRGGBB) into [r, g, b]. Returns null if invalid.
 */
export const parseHexColor = (hex) => {
  if (typeof hex !== "string") return null;
  const match = hex.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;
  let value = match[1];
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
};

/**
 * Pick black or white text color for the highest WCAG contrast against the
 * given background color. Returns white when the input is not a valid hex —
 * call sites normalize via resolveButtonBackground first, so the fallback is
 * unreachable in practice.
 */
export const pickReadableTextColor = (backgroundHex) => {
  const rgb = parseHexColor(backgroundHex);
  if (!rgb) return "#FFFFFF";

  const channelLuminance = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgb.map(channelLuminance);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const contrastWithWhite = 1.05 / (lum + 0.05);
  const contrastWithBlack = (lum + 0.05) / 0.05;
  return contrastWithBlack >= contrastWithWhite ? "#000000" : "#FFFFFF";
};

/**
 * Resolve the button background color: the event accent color if it is a valid
 * hex, otherwise the Congrs brand fallback.
 */
export function resolveButtonBackground(accentColor) {
  return parseHexColor(accentColor) ? accentColor : DEFAULT_BUTTON_COLOR;
}

/**
 * Inline style string for an email button anchor. Inline styles keep it
 * email-client safe (Gmail/Outlook strip <style> blocks).
 */
export function buttonInlineStyle(bg, color) {
  return `display: inline-block; background-color: ${bg}; color: ${color}; text-decoration: none; padding: 10px 20px; border-radius: 4px; font-weight: bold;`;
}

/**
 * Build the email button HTML — a centered <p> wrapping a styled <a>. This
 * is the single source of truth for buttons across email templates and the
 * campaign editor. Background uses the event accent color (auto-contrast
 * text), falling back to the Congrs brand color when no accent is set.
 */
export function buildEmailButton({ href, label, accentColor }) {
  const bg = resolveButtonBackground(accentColor);
  const color = pickReadableTextColor(bg);
  const style = buttonInlineStyle(bg, color);
  const safeHref = href || "#";
  const safeLabel = label || "Button";

  return `<p data-email-button-wrapper="true" style="text-align: center;"><a href="${safeHref}" target="_blank" rel="noopener noreferrer" data-email-button="true" style="${style}">${safeLabel}</a></p>`;
}
